import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';
import { apiClient } from '../api/client';
import {
  patientActionStatusOptions,
  type PatientActionStatus,
} from '../constants/patient';
import IntakeToggleGroup from './patientIntake/IntakeToggleGroup';
import {
  patientDetailControlRowSx,
  patientDetailLabelRowSx,
  patientDetailToggleGroupSx,
} from './patientIntake/intakeFormLayout';

type Props = {
  patientId: number;
  initialValue: PatientActionStatus;
  readOnly?: boolean;
  onSaved?: (value: PatientActionStatus) => void;
};

/** 患者情報画面：対応ステータス（変更時に自動保存） */
const PatientActionStatusEditor: React.FC<Props> = ({
  patientId,
  initialValue,
  readOnly,
  onSaved,
}) => {
  const [value, setValue] = useState<PatientActionStatus>(initialValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  const skipSaveRef = useRef(true);
  const savedTimerRef = useRef<number | null>(null);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  useEffect(() => {
    setValue(initialValue);
    skipSaveRef.current = true;
  }, [initialValue, patientId]);

  useEffect(() => {
    return () => {
      if (savedTimerRef.current != null) window.clearTimeout(savedTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (readOnly) return;
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }

    void (async () => {
      setSaving(true);
      setError('');
      setSavedFlash(false);
      try {
        await apiClient.patch(`/api/patients/${patientId}`, { action_status: value });
        onSavedRef.current?.(value);
        setSavedFlash(true);
        if (savedTimerRef.current != null) window.clearTimeout(savedTimerRef.current);
        savedTimerRef.current = window.setTimeout(() => setSavedFlash(false), 2000);
      } catch (e: unknown) {
        if (axios.isAxiosError(e) && e.response?.data?.error) {
          setError(String(e.response.data.error));
        } else {
          setError('保存に失敗しました');
        }
      } finally {
        setSaving(false);
      }
    })();
  }, [value, readOnly, patientId]);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={patientDetailLabelRowSx}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          対応ステータス
        </Typography>
        {saving && <CircularProgress size={12} />}
        {!saving && savedFlash && (
          <Typography variant="caption" color="success.main">
            保存しました
          </Typography>
        )}
      </Box>
      <Box sx={patientDetailControlRowSx}>
        <IntakeToggleGroup
          options={patientActionStatusOptions}
          value={value}
          onChange={(v) => setValue(v as PatientActionStatus)}
          ariaLabel="対応ステータス"
          disabled={readOnly}
          sx={patientDetailToggleGroupSx}
        />
      </Box>
      {error ? (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

export default PatientActionStatusEditor;
